import { CommonTooltips } from "@mcleod/common";
import {AutogenLayoutFuelTransactionDetail} from "./autogen/AutogenLayoutFuelTransactionDetail";
import {
    DataSourceExecutionEvent,
    DataSourceAction,
    TableRowDisplayEvent,
    Textbox,
    LookupModelSelectionEvent,
    TableRow,
    TableRowMode, TableContentsChangedEvent, Layout
} from "@mcleod/components";
import {ModelRow, Currency, CurrencyUtil} from "@mcleod/core";


export class FuelDetail extends AutogenLayoutFuelTransactionDetail {

    private fundedTotal: number = 0;
    private directTotal: number = 0;

    override onLoad(): void | Promise<void> {
        this.textboxMovementIdBeforeLookupModelSearchListener();
    }

    get fuelAndProductFundedTotal(): Currency {
        const currentFundedTotal = this.activeRow?.getCurrency("funded_amount") ?? new Currency(0);
        return new Currency(this.fundedTotal + currentFundedTotal.amount);
    }

    get fuelAndProductDirectTotal(): Currency {
        const currentDirectTotal = this.activeRow?.getCurrency("direct_amount") ?? new Currency(0);
        return new Currency(this.directTotal + currentDirectTotal.amount);
    }

    public sourceFuelDetailBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.UPDATE) {
            let totalProductCost = 0;
            this.sourceFuelProducts.data.forEach((row) => {
                const cost = row.getCurrency("product_cost");
                totalProductCost += cost.amount;
            });
            this.activeRow.set("misc_cost", new Currency(totalProductCost));
            this.activeRow.set("funded_amount", this.fuelAndProductFundedTotal);
            this.activeRow.set("direct_amount", this.fuelAndProductDirectTotal);
        }
    }

    public sourceFuelDetailAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            const fuelTotals = this.activeRow.get("fuel_totals", []);
            const fuelRows = fuelTotals.map((data) => this.createFuelTransactionRow(data));
            this.tableFuelTotals.displayData(null, fuelRows, 0);

            const nonFuelTotals = this.activeRow.get("non_fuel_totals", []);
            const nonFuelRows = nonFuelTotals.map((data) => this.createFuelTransactionRow(data));
            this.tableNonFuelTotals.displayData(null, nonFuelRows, 0);

            const feesAndTaxes = this.activeRow.get("fees_and_taxes", []);
            const feesAndTaxesRows = feesAndTaxes.map((data) => this.createFuelTransactionRow(data));
            this.tableFeesAndTaxes.displayData(null, feesAndTaxesRows, 0);
            this.textboxChainCode.quickInfoOverrideSearchMethod = async (layout, keyValue) => {
                if (!this.activeRow.isNull(this.textboxChainCode.field)) {
                    await layout.search({ interface_id: this.activeRow.get("interface_id"), code: this.activeRow.get("chain_code") });
                }
            }
        }

        if (event.getAction() === DataSourceAction.UPDATE) {
            this.search(this.mainDataSource.lastSearch);
        }
    }

    sourceFuelProductsAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.calculateTotals();

            // Prevents the main data source from thinking these fields were changed by the user
            this.activeRow?.resetOriginalData("funded_amount");
            this.activeRow?.resetOriginalData("direct_amount");
            this.mainDataSource?.notifyHasChangedComponents(false);
        }
    }

    private calculateTotals() {
        this.fundedTotal = 0;
        this.directTotal = 0;

        this.sourceFuelProducts.data.forEach((row) => {
            const billCode = row.get("product_bill_code");
            const cost = row.get("product_cost");
            if ("F" === billCode) {
                this.fundedTotal += cost.amount;
            } else {
                this.directTotal += cost.amount;
            }
        });

        this.textboxFundedAmount.text = CurrencyUtil.formatCurrency(this.fuelAndProductFundedTotal);
        this.textboxDirectAmount.text = CurrencyUtil.formatCurrency(this.fuelAndProductDirectTotal);
        this.labelProductCodesTotalValue.text = CurrencyUtil.formatCurrency(new Currency(this.fundedTotal + this.directTotal));
    }

    private createFuelTransactionRow(data) {
        return new ModelRow(this.mainDataSource.url, false, data);
    }

    tableProductCodesOnContentsChanged(event: TableContentsChangedEvent) {
        this.calculateTotals();
    }

    /** This is an event handler for the onRowDisplay event of tableProductCodes. */
    tableProductCodesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const productCode = tableRow.findComponentById("textboxProductCode") as Textbox;
        productCode?.addBeforeLookupModelSearchListener((event) => {
            event.filter.interface_id = this.activeRow.get("interface_id");
        });
    }

    /** This is an event handler for the onLookupModelSelection event of textboxProductCode. */
    textboxProductCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        const modelRow = tableRow.mode == TableRowMode.QUICK_ADD ? this.tableProductCodes.quickAddRow.data : tableRow.data as ModelRow;
        const descr = textbox.getFirstLookupModelData()?.get("product_descr");
        modelRow.set("product_descr", descr ?? "");
        if (tableRow.mode == TableRowMode.QUICK_ADD) {
            tableRow.displayComponentValues();
        }
    }

    /** This is an event handler for the beforeLookupModelSearchListener event of textboxMovementId. */
    textboxMovementIdBeforeLookupModelSearchListener() {
        const orderIdFilter = event => event.filter.order_id = this.activeRow.get("order_id");
        this.textboxMovementId.addBeforeLookupModelSearchListener(orderIdFilter);
    }

    textboxOrderIdOnChangeListener() {
        this.textboxMovementId.clear();
    }

    textboxOrderIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const orderId = event.selection?.get("id");
        if (orderId) {
            const movementLayout = Layout.getLayout(this.textboxMovementId.lookupModelLayout);
            movementLayout.addLayoutLoadListener(() => {
                const filter = {order_id: orderId};
                movementLayout.mainDataSource.search(filter, null, {layoutName: movementLayout.layoutName}).then(response => {
                    if (response?.modelRows?.length === 1) {
                        const movementLookupRow = response.modelRows[0];
                        const resultField = this.textboxMovementId.lookupModelResultField || "id";
                        const movementValue = movementLookupRow.get(resultField);
                        this.activeRow.setValueAndLookupModelData("movement_id", movementValue, movementLookupRow);
                        this.textboxMovementId.displayData(this.activeRow, null, 0);
                    }
                }).catch(err => {
                    console.warn("movement search failed", err);
                    this.textboxMovementId.showLookupModelDropdown(String(orderId));
                });
            });
        }
    }

}
